import type { Reparability } from "../../api/Reparability";
import type { Event } from "../../api/useEvents";

type EventWithFailures = { failureNames?: readonly string[] };
type GroupableEvent =
  | { type: "DIAGNOSED"; sourceFaultEventIds: readonly string[] }
  | { type: "MANUAL" };

export const isGroupedEvent = (event: GroupableEvent) =>
  event.type === "DIAGNOSED" && event.sourceFaultEventIds.length > 0;

export const isMultiFaultEvent = (event: EventWithFailures) =>
  (event.failureNames?.length ?? 0) > 1;

export const areAllReparabilityUnset = (reparabilities: Reparability[]) =>
  reparabilities.every(reparability => reparability === null);

export const getComputeRowKey = (event: Event) => {
  switch (event.type) {
    case "DIAGNOSED":
      return `diagnosed-${event.id}`;
    case "MANUAL":
      return `manual-${event.id}`;
    default: {
      const n: never = event;
      throw n;
    }
  }
};
export const getEventId = (event: Event) => {
  switch (event.type) {
    case "DIAGNOSED":
      return event.id;
    case "MANUAL":
      return event.id;
    default: {
      const n: never = event;
      throw n;
    }
  }
};
export const getEventInProgress = (event: Event) => {
  switch (event.type) {
    case "MANUAL":
      return event.closedAt === null;
    case "DIAGNOSED":
      return event.metadata.status !== "CLOSED";
    default:
      return false;
  }
};
export const getEventPriority = (event: Event) => {
  switch (event.type) {
    case "DIAGNOSED":
      return event.priority ?? event.metadata.priority;
    case "MANUAL":
      return event.priority;
    default:
      return "LOW"; // Default priority if not specified
  }
};
export const getEventEndsAt = (event: Event) => {
  switch (event.type) {
    case "DIAGNOSED":
      return event.endsAt;
    case "MANUAL":
      return event.metadata.endsAt;
    default: {
      const n: never = event;
      throw n;
    }
  }
};
export const getEventDurationEndsAt = (event: Event) => {
  switch (event.type) {
    case "DIAGNOSED":
      return new Date().getTime() - new Date(event.endsAt).getTime();
    case "MANUAL":
      return event.closedAt ? new Date().getTime() - new Date(event.closedAt).getTime() : null;
    default:
      return null;
  }
};
export const getEventSystems = (event: Event) => {
  switch (event.type) {
    case "DIAGNOSED":
      return Array.from(
        new Set(
          event.metadata.faults
            .map(fault => fault.systemId)
            .filter((systemId): systemId is string => systemId !== null),
        ),
      );
    case "MANUAL": {
      const systemId = event.metadata.manualFault.systemId;
      return systemId === null ? [] : [systemId];
    }
    default:
      return [];
  }
};
export const getEventLastActivityAt = (event: Event) => {
  switch (event.type) {
    case "DIAGNOSED":
      return event.metadata.lastActivityAt;
    case "MANUAL":
      return new Date(event.updatedAt);
    default:
      return null;
  }
};
export const getEventIdentifiedAction = (event: Event) => {
  switch (event.type) {
    case "DIAGNOSED":
      return event.action;
    case "MANUAL":
      return event.identifiedAction;
    default:
      return null;
  }
};
export const getEventDetection = (event: Event) => {
  switch (event.type) {
    case "DIAGNOSED":
      return event.detection ?? true;
    case "MANUAL":
      return true;
    default:
      return true;
  }
};
export const getEventScope = (event: Event) => {
  switch (event.type) {
    case "DIAGNOSED":
      return `diagnosed-${getEventId(event)}`;
    case "MANUAL":
      return `manual-${event.id}`;
    default: {
      const n: never = event;
      throw n;
    }
  }
};
export const getEventFaultNames = (event: Event): string[] => {
  switch (event.type) {
    case "DIAGNOSED":
      return event.metadata.faults.map(fault => fault.name);
    case "MANUAL":
      return [event.metadata.manualFault.name];
    default: {
      const n: never = event;
      throw n;
    }
  }
};
